import React, { useMemo, useState, useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import styled from "styled-components/native"

import { AlbumResponse, AlbumTrackResponse } from "@treefort/api-spec"
import { joinContributorNames } from "@treefort/lib/contributor"
import { formatDate } from "@treefort/lib/date"
import icons from "@treefort/tokens/app/icons"

import { useTrack } from "../../hooks/audio"
import { useActiveProfileId } from "../../hooks/use-active-profile-id"
import useAppManifest from "../../hooks/use-app-manifest"
import { useAsyncViewPropsForQueries } from "../../hooks/use-async-view-props-for-queries"
import { useBooleanState } from "../../hooks/use-boolean-state"
import useContent from "../../hooks/use-content"
import { useOpenCheckoutPage } from "../../hooks/use-open-checkout-page"
import { useRouteParams } from "../../hooks/use-route-params"
import {
  canDownloadConsumableContent,
  getConsumableContentFromAlbumResponse,
  getTrackId,
  getTracksFromConsumableContent,
} from "../../lib/consumable-content"
import { playContentAudio } from "../../lib/content-audio"
import { createStyle } from "../../lib/create-style"
import { kebabCase } from "../../lib/kebab-case"
import { syncManager } from "../../watermelon/sync"
import AddToLibraryButton, { canAddToLibrary } from "../add-to-library-button"
import { AsyncButton } from "../async-button"
import { useAsyncViewProps } from "../async-view"
import Box, { BoxPadding } from "../box"
import Column from "../column"
import DownloadButton from "../download-button"
import { Heading } from "../heading"
import IconButton from "../icon-button"
import LockedContentButton from "../locked-content-button"
import { MetadataSpacer, MetadataText } from "../metadata"
import ModuleArtworkLayout from "../module-artwork-layout"
import ProgressForConsumableContent from "../progress-for-consumable-content"
import Row from "../row"
import { SelectOption } from "../select"
import Text from "../text"
import { TextToggleModal } from "../text-toggle"

const buttonFlexContainerStyle = createStyle({ flex: 1 })

const PlayButton = styled(AsyncButton)`
  flex: ${(props) =>
    props.theme.audiobookModule.playButtonFlex ? "1" : "none"};
`

const LockedPlayButton = styled(LockedContentButton)`
  flex: ${(props) =>
    props.theme.audiobookModule.playButtonFlex ? "1" : "none"};
`

export function AlbumModule({
  contentId,
  maxWidth,
  paddingTop = "medium",
}: {
  contentId: number
  maxWidth?: number
  paddingTop?: BoxPadding
}): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const profileId = useActiveProfileId()
  const [artworkReady, setArtworkReady] = useState(false)
  const album = useContent(contentId, "album")
  const firstTrack = album.data?.details.tracks[0]
  const consumableContent = album.isSuccess
    ? getConsumableContentFromAlbumResponse(album.data)
    : undefined

  useEffect(() => {
    syncManager.requestSync({ syncType: "user-initiated" })
  }, [])

  const tracks = useMemo(
    () => getTracksFromConsumableContent({ consumableContent, profileId }),
    [consumableContent, profileId],
  )
  const trackCount = tracks.length
  const manifest = useAppManifest()
  const showDescriptionButton = album.data && Boolean(album.data.description)
  const showDownloadButton =
    trackCount &&
    consumableContent &&
    canDownloadConsumableContent(consumableContent)
  const showAddToLibraryButton = canAddToLibrary({
    manifest,
    consumableContent,
  })

  const availability = firstTrack?.audioMedia
  const openCheckoutPage = useOpenCheckoutPage({
    availability,
    contentId,
  })

  const playAlbum = useCallback(
    (trackId?: string) => {
      if (consumableContent) {
        return playContentAudio({ consumableContent, profileId, trackId })
      }
    },
    [consumableContent, profileId],
  )

  // If a track is specified in route params then autoplay that track
  const [params, setParams] = useRouteParams()
  const autoplayTrackIndex =
    params.track && !isNaN(parseInt(params.track))
      ? parseInt(params.track) - 1
      : null
  const autoplayTrackRequested = useRef(false)
  useEffect(() => {
    if (
      autoplayTrackIndex !== null &&
      availability?.status === "available" &&
      !autoplayTrackRequested.current
    ) {
      // Make sure we don't send multiple play requests
      autoplayTrackRequested.current = true

      // Clear the track param so we don't continuously reset playback to the
      // requested track every time the page reloads
      setParams({ track: undefined })

      playAlbum(getTrackId(contentId, "album", autoplayTrackIndex))
    }
  }, [contentId, availability, playAlbum, autoplayTrackIndex, setParams])

  useAsyncViewProps(
    useAsyncViewPropsForQueries([album], {
      forceLoading: album.data?.artworkMedia ? !artworkReady : false,
    }),
  )

  const artist = joinContributorNames(album.data?.contributors, "artist")
  const publisher = joinContributorNames(album.data?.contributors, "publisher")

  const shouldShowDate = Boolean(
    album.data?.publishedAt && album.data.dateDisplayStrategy !== "hidden",
  )

  return (
    <ModuleArtworkLayout
      paddingTop={paddingTop}
      artwork={album.data?.artworkMedia?.original.url}
      onArtworkReady={setArtworkReady}
      maxWidth={maxWidth}
      fullBleedContent
    >
      {({ layout }) => (
        <>
          <Box
            paddingHorizontal={
              layout === "portrait" ? "pagePaddingHorizontal" : "none"
            }
          >
            <Heading level={1} textStyle="headingLarge" color="primary">
              {album.data?.title || ""}
            </Heading>
            <Row paddingTop="small">
              {artist ? <MetadataText>{artist}</MetadataText> : null}
              {artist && shouldShowDate ? <MetadataSpacer /> : null}
              {shouldShowDate && album.data?.publishedAt ? (
                <MetadataText>
                  {formatDate(new Date(album.data.publishedAt), {
                    strategy: album.data.dateDisplayStrategy || undefined,
                    i18n,
                  })}
                </MetadataText>
              ) : null}
              <ProgressForConsumableContent
                consumableContent={consumableContent}
                includeDurationLabel
                childrenBefore={
                  album.data?.publishedAt ? <MetadataSpacer /> : undefined
                }
              />
            </Row>
            {publisher ? <MetadataText>{publisher}</MetadataText> : null}
            <Row gap="large" paddingTop="medium">
              {availability?.status === "notAvailable" ? (
                <LockedPlayButton
                  id={kebabCase(
                    `album-locked-${consumableContent?.content.id}-${consumableContent?.content.title || "untitled"}`,
                  )}
                  openCheckoutPage={openCheckoutPage}
                  availability={availability}
                  action="listen"
                  containerStyle={buttonFlexContainerStyle}
                  externalPurchaseUrl={album.data?.externalPurchaseUrl}
                />
              ) : (
                <PlayButton
                  id={kebabCase(
                    `album-play-${consumableContent?.content.id}-${consumableContent?.content.title || "untitled"}`,
                  )}
                  icon={icons.headphones}
                  type="primary"
                  onPress={playAlbum}
                  disabled={!album.isSuccess}
                  containerStyle={buttonFlexContainerStyle}
                >
                  {t("Listen")}
                </PlayButton>
              )}
              <Row gap="small">
                {showDescriptionButton ? (
                  <DescriptionButton album={album.data} />
                ) : null}
                {showDownloadButton ? (
                  <DownloadButton consumableContent={consumableContent} />
                ) : null}
                {showAddToLibraryButton ? (
                  <AddToLibraryButton consumableContent={consumableContent} />
                ) : null}
              </Row>
            </Row>
          </Box>
          {album.data && consumableContent ? (
            <AlbumTracks
              album={album.data}
              albumLayout={layout}
              onPressTrack={
                availability?.status === "notAvailable" &&
                availability.reasonCode === "offeringNotAvailable"
                  ? undefined
                  : availability?.status === "notAvailable"
                    ? openCheckoutPage
                    : playAlbum
              }
            />
          ) : null}
        </>
      )}
    </ModuleArtworkLayout>
  )
}

function AlbumTracks({
  album,
  albumLayout,
  onPressTrack,
}: {
  album: AlbumResponse
  albumLayout: "portrait" | "landscape"
  onPressTrack: ((id: string) => unknown) | undefined
}) {
  const currentTrack = useTrack()

  return album.details.tracks.length > 1 ? (
    <Column paddingTop="medium" alignItems="stretch">
      {album.details.tracks.map((track, index) => (
        <AlbumTrack
          albumId={album.id}
          albumTitle={album.title}
          key={index}
          track={track}
          trackCount={album.details.tracks.length}
          index={index}
          currentTrackId={currentTrack?.id}
          onPress={onPressTrack}
          albumLayout={albumLayout}
        />
      ))}
    </Column>
  ) : null
}

function AlbumTrack({
  albumTitle,
  albumId,
  track,
  index,
  trackCount,
  currentTrackId,
  onPress,
  albumLayout,
}: {
  albumTitle: string
  albumId: number
  track: AlbumTrackResponse
  index: number
  trackCount: number
  currentTrackId: string | undefined
  onPress: ((id: string) => unknown) | undefined
  albumLayout: "portrait" | "landscape"
}) {
  const trackId = getTrackId(albumId, "album", index)
  return (
    <SelectOption
      selectId={kebabCase(`album-${albumTitle}`)}
      option={{ value: trackId, label: track.title }}
      selected={trackId === currentTrackId}
      onPress={onPress}
      numbering={{ index, optionCount: trackCount }}
      paddingVertical="small"
      paddingHorizontal={
        albumLayout === "portrait" ? "pagePaddingHorizontal" : "none"
      }
    />
  )
}

function DescriptionButton({ album }: { album: AlbumResponse }) {
  const { t } = useTranslation()
  const [modalOpen, openModal, closeModal] = useBooleanState(false)
  return (
    <>
      <IconButton
        id={kebabCase(`album-description-${album.id}-${album.title}`)}
        source={icons.infoCircle}
        label={t("Read more")}
        color="secondary"
        onPress={openModal}
        minSize="buttonHeight"
      />
      <TextToggleModal
        title={album.title}
        open={modalOpen}
        onPressCloseButton={closeModal}
        onPressOutside={closeModal}
      >
        <Text textStyle="body">{album.description}</Text>
      </TextToggleModal>
    </>
  )
}
